import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { useNavigate, Link, useParams } from 'react-router-dom';
import { getFightAnalysis, submitFighterAnalysis, getCompletedFights } from '../services/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../utils/errorUtils';
import { 
  ArrowLeft,
  Trophy,
  TrendingUp,
  TrendingDown,
  Target,
  MessageSquare,
  Star,
  CheckCircle2,
  Clock,
  Swords,
  Award
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Slider } from '../components/ui/slider';
import { format, parseISO, isValid } from 'date-fns';
import { cn } from '../lib/utils';

const safeDateFormat = (dateStr, formatStr) => {
  if (!dateStr) return 'N/A';
  const date = typeof dateStr === 'string' ? parseISO(dateStr) : new Date(dateStr);
  return isValid(date) ? format(date, formatStr) : 'N/A';
};

const FightAnalysisPage = () => {
  const { fightId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [analysisData, setAnalysisData] = useState(null);
  const [completedFights, setCompletedFights] = useState([]);
  const [saving, setSaving] = useState(false);
  
  // Form state for fighter analysis
  const [formData, setFormData] = useState({
    overall_performance: 5,
    what_went_well: '',
    what_needs_improvement: '',
    focus_areas_next_camp: ''
  });

  const loadData = useCallback(async () => {
    try {
      if (fightId) {
        const res = await getFightAnalysis(fightId);
        setAnalysisData(res.data);
        
        // Pre-fill form if fighter already submitted
        if (res.data.fighter_analysis) {
          setFormData({
            overall_performance: res.data.fighter_analysis.overall_performance || 5,
            what_went_well: res.data.fighter_analysis.what_went_well || '',
            what_needs_improvement: res.data.fighter_analysis.what_needs_improvement || '',
            focus_areas_next_camp: res.data.fighter_analysis.focus_areas_next_camp || ''
          });
        }
      } else {
        // Load list of completed fights
        const res = await getCompletedFights();
        setCompletedFights(res.data);
      }
    } catch (error) {
      console.error('Failed to load:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [fightId]);

  useEffect(() => {
    if (user?.role !== 'fighter') {
      navigate('/dashboard');
      return;
    }
    loadData();
  }, [user, navigate, loadData]);

  const handleSubmit = async () => {
    if (!formData.what_went_well.trim() || !formData.what_needs_improvement.trim()) {
      toast.error('Please fill in what went well and what needs improvement');
      return;
    }
    
    setSaving(true);
    try {
      await submitFighterAnalysis(fightId, formData);
      toast.success('Analysis saved!');
      loadData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to save analysis'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-red-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Fight list view (no fightId)
  if (!fightId) {
    return (
      <div className="min-h-screen bg-black">
        <header className="border-b border-zinc-800 bg-zinc-950 sticky top-0 z-50">
          <div className="max-w-2xl mx-auto px-4 py-4">
            <div className="flex items-center gap-4">
              <Link to="/fighter/dashboard">
                <Button variant="ghost" size="sm" className="text-zinc-400">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
              </Link>
              <div>
                <h1 className="text-white font-heading text-lg uppercase tracking-tight">Fight Analysis</h1>
                <p className="text-zinc-500 text-sm">Review and learn from your fights</p>
              </div>
            </div>
          </div>
        </header>

        <main className="max-w-2xl mx-auto px-4 py-8">
          {completedFights.length > 0 ? (
            <div className="space-y-4">
              {completedFights.map((fight) => (
                <Link 
                  key={fight.id} 
                  to={`/fighter/analysis/${fight.id}`}
                  className="block"
                >
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={cn(
                      "bg-zinc-900/50 border rounded-xl p-5 hover:border-zinc-700 transition-all",
                      fight.result === 'win' ? "border-green-600/30" : 
                      fight.result === 'loss' ? "border-red-600/30" : "border-zinc-800"
                    )}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className={cn(
                          "w-12 h-12 rounded-full flex items-center justify-center",
                          fight.result === 'win' ? "bg-green-600/20" : 
                          fight.result === 'loss' ? "bg-red-600/20" : "bg-zinc-800"
                        )}>
                          {fight.result === 'win' ? (
                            <Trophy className="w-6 h-6 text-green-500" />
                          ) : fight.result === 'loss' ? (
                            <TrendingDown className="w-6 h-6 text-red-500" />
                          ) : (
                            <Swords className="w-6 h-6 text-zinc-500" />
                          )}
                        </div>
                        <div>
                          <p className="text-white font-semibold">vs {fight.opponent_name}</p>
                          <p className="text-zinc-400 text-sm">{fight.event_name}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <span className={cn(
                          "text-sm font-medium uppercase px-2 py-1 rounded",
                          fight.result === 'win' ? "bg-green-600/20 text-green-400" : 
                          fight.result === 'loss' ? "bg-red-600/20 text-red-400" : 
                          "bg-zinc-800 text-zinc-400"
                        )}>
                          {fight.result || 'Draw'}
                        </span>
                        <p className="text-zinc-500 text-xs mt-1">{safeDateFormat(fight.fight_date, 'M/d/yyyy')}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-4 text-sm">
                      <div className="flex items-center gap-1">
                        {fight.has_fighter_analysis ? (
                          <CheckCircle2 className="w-4 h-4 text-green-500" />
                        ) : (
                          <Clock className="w-4 h-4 text-yellow-500" />
                        )}
                        <span className="text-zinc-400">Your Analysis</span>
                      </div>
                      <div className="flex items-center gap-1">
                        {fight.has_coach_analysis ? (
                          <CheckCircle2 className="w-4 h-4 text-green-500" />
                        ) : (
                          <Clock className="w-4 h-4 text-zinc-600" />
                        )}
                        <span className="text-zinc-400">Coach Breakdown</span>
                      </div>
                    </div>
                  </motion.div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <Swords className="w-12 h-12 mx-auto mb-4 text-zinc-600" />
              <h3 className="text-white text-lg mb-2">No Completed Fights</h3>
              <p className="text-zinc-400">Your fight analysis will appear here after a fight result is recorded</p>
            </div>
          )}
        </main>
      </div>
    );
  }

  // Single fight analysis view
  const { fight, fighter_analysis, coach_analysis, has_result } = analysisData || {};

  if (!has_result) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <Clock className="w-12 h-12 mx-auto mb-4 text-zinc-600" />
          <h3 className="text-white text-lg mb-2">Fight Not Completed</h3>
          <p className="text-zinc-400 mb-4">Analysis will be available after the fight result is recorded</p>
          <Link to="/fighter/analysis">
            <Button variant="ghost">Back to Fights</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black">
      <header className="border-b border-zinc-800 bg-zinc-950 sticky top-0 z-50">
        <div className="max-w-2xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <Link to="/fighter/analysis">
              <Button variant="ghost" size="sm" className="text-zinc-400">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
            </Link>
            <div>
              <h1 className="text-white font-heading text-lg uppercase tracking-tight">Post-Fight Analysis</h1>
              <p className="text-zinc-500 text-sm">vs {fight?.opponent_name}</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-6 space-y-6">
        {/* Fight Summary */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className={cn(
            "rounded-xl p-6 border",
            fight?.result === 'win' 
              ? "bg-gradient-to-br from-green-900/30 to-green-950/10 border-green-600/30"
              : "bg-gradient-to-br from-red-900/30 to-red-950/10 border-red-600/30"
          )}
        >
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2 mb-2">
                {fight?.result === 'win' ? (
                  <Trophy className="w-6 h-6 text-green-500" />
                ) : (
                  <TrendingDown className="w-6 h-6 text-red-500" />
                )}
                <span className={cn(
                  "text-lg font-bold uppercase",
                  fight?.result === 'win' ? "text-green-400" : "text-red-400"
                )}>
                  {fight?.result}
                </span>
                {fight?.result_method && (
                  <span className="text-zinc-400 text-sm">via {fight.result_method}</span>
                )}
              </div>
              <p className="text-white text-xl font-semibold">vs {fight?.opponent_name}</p>
              <p className="text-zinc-400">{fight?.event_name} • {safeDateFormat(fight?.fight_date, 'M/d/yyyy')}</p>
            </div>
            <div className="text-right">
              <p className="text-zinc-500 text-sm">{fight?.weight_class}</p>
              <p className="text-zinc-500 text-sm">{fight?.rounds} rounds</p>
            </div>
          </div>
        </motion.div>

        {/* Fighter Self-Analysis Form */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6"
        >
          <h2 className="text-white font-heading uppercase flex items-center gap-2 mb-6">
            <Star className="w-5 h-5 text-yellow-500" />
            Your Self-Assessment
          </h2>

          <div className="space-y-6">
            {/* Performance Rating */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="text-zinc-300">Overall Performance</label>
                <span className="text-2xl font-bold text-white">{formData.overall_performance}/10</span>
              </div>
              <Slider
                value={[formData.overall_performance]}
                onValueChange={(val) => setFormData(prev => ({ ...prev, overall_performance: val[0] }))}
                min={1}
                max={10}
                step={1}
                className="w-full"
                data-testid="performance-slider"
              />
              <div className="flex justify-between text-xs text-zinc-500 mt-1">
                <span>Poor</span>
                <span>Average</span>
                <span>Excellent</span>
              </div>
            </div>

            {/* What went well */}
            <div>
              <label className="text-zinc-300 text-sm mb-2 block flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-green-500" />
                What went well?
              </label>
              <Textarea
                value={formData.what_went_well}
                onChange={(e) => setFormData(prev => ({ ...prev, what_went_well: e.target.value }))}
                placeholder="e.g., My jab was landing clean, cardio held up well..."
                className="bg-zinc-950 border-zinc-800 min-h-[100px]"
                data-testid="what-went-well"
              />
            </div>

            {/* What needs improvement */}
            <div>
              <label className="text-zinc-300 text-sm mb-2 block flex items-center gap-2">
                <Target className="w-4 h-4 text-orange-500" />
                What needs improvement?
              </label>
              <Textarea
                value={formData.what_needs_improvement}
                onChange={(e) => setFormData(prev => ({ ...prev, what_needs_improvement: e.target.value }))}
                placeholder="e.g., Got taken down too easily, need to work on sprawls..."
                className="bg-zinc-950 border-zinc-800 min-h-[100px]"
                data-testid="what-needs-improvement"
              />
            </div>

            {/* Focus for next camp */}
            <div>
              <label className="text-zinc-300 text-sm mb-2 block flex items-center gap-2">
                <Award className="w-4 h-4 text-purple-500" />
                Focus areas for next camp (optional)
              </label>
              <Textarea
                value={formData.focus_areas_next_camp}
                onChange={(e) => setFormData(prev => ({ ...prev, focus_areas_next_camp: e.target.value }))}
                placeholder="e.g., Takedown defense, inside leg kicks, clinch work..."
                className="bg-zinc-950 border-zinc-800 min-h-[80px]"
                data-testid="focus-areas"
              />
            </div>

            <Button 
              onClick={handleSubmit}
              disabled={saving}
              className="w-full bg-red-600 hover:bg-red-700 h-12"
              data-testid="save-analysis-btn"
            >
              {saving ? 'Saving...' : fighter_analysis ? 'Update Analysis' : 'Save Analysis'}
            </Button>
          </div>
        </motion.div>

        {/* Coach Analysis (Read Only) */}
        {coach_analysis && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-zinc-900/50 border border-blue-600/30 rounded-xl p-6"
          >
            <h2 className="text-white font-heading uppercase flex items-center gap-2 mb-6">
              <MessageSquare className="w-5 h-5 text-blue-500" />
              Coach's Technical Breakdown
            </h2>

            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-zinc-800/50 rounded-lg">
                <span className="text-zinc-400">Game Plan Execution</span>
                <span className="text-2xl font-bold text-white">{coach_analysis.game_plan_execution}/10</span>
              </div>

              {coach_analysis.striking_notes && (
                <div>
                  <p className="text-zinc-400 text-sm mb-1">Striking</p>
                  <p className="text-white">{coach_analysis.striking_notes}</p>
                </div>
              )}

              {coach_analysis.grappling_notes && (
                <div>
                  <p className="text-zinc-400 text-sm mb-1">Grappling</p>
                  <p className="text-white">{coach_analysis.grappling_notes}</p>
                </div>
              )}

              {coach_analysis.cardio_notes && (
                <div>
                  <p className="text-zinc-400 text-sm mb-1">Cardio</p>
                  <p className="text-white">{coach_analysis.cardio_notes}</p>
                </div>
              )}

              <div className="pt-4 border-t border-zinc-800">
                <p className="text-zinc-400 text-sm mb-1">Technical Breakdown</p>
                <p className="text-white">{coach_analysis.technical_breakdown}</p>
              </div>

              <div className="pt-4 border-t border-zinc-800">
                <p className="text-zinc-400 text-sm mb-1">Recommendations</p>
                <p className="text-green-400">{coach_analysis.recommendations}</p>
              </div>

              {coach_analysis.round_by_round && coach_analysis.round_by_round.length > 0 && (
                <div className="pt-4 border-t border-zinc-800">
                  <p className="text-zinc-400 text-sm mb-2">Round by Round</p>
                  <div className="space-y-2">
                    {coach_analysis.round_by_round.map((notes, idx) => (
                      <div key={idx} className="p-3 bg-zinc-800/30 rounded-lg">
                        <span className="text-yellow-500 font-medium">Round {idx + 1}:</span>
                        <span className="text-zinc-300 ml-2">{notes}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <p className="text-zinc-500 text-xs text-right">
                Analysis by {coach_analysis.coach_name} • {safeDateFormat(coach_analysis.created_at, 'M/d/yyyy')}
              </p>
            </div>
          </motion.div>
        )}

        {!coach_analysis && (
          <div className="bg-zinc-900/30 border border-zinc-800 border-dashed rounded-xl p-6 text-center">
            <MessageSquare className="w-8 h-8 mx-auto mb-2 text-zinc-600" />
            <p className="text-zinc-500">Waiting for coach's technical breakdown</p>
          </div>
        )}
      </main>
    </div>
  );
};

export default FightAnalysisPage;
